import React, { useState, useEffect } from "react";
import { MdKeyboardDoubleArrowLeft, MdKeyboardDoubleArrowRight } from "react-icons/md";
import { CircularProgress } from "@mui/material";
import LinksComponent from "../../../components/Tenant/LinksComponent/LinksComponent";
import {
  useGetSessionMetricsMutation,
  useGetUserSessionDetailMutation,
} from "../../../features/tenant/ActiveUsers/activeUsersApi";
import SessionDetailModal from "./SessionDetailModal";
import ExportReportModal  from "./ExportReportModal";

// ─── main component ───────────────────────────────────────────────────────────

const UserLoginLogs = () => {
  const token     = sessionStorage.getItem("access_token");
  const tenant_id = JSON.parse(sessionStorage.getItem("vendorData"))?.tenant_id;

  // ── filter state ──
  const [userId,   setUserId]   = useState("");
  const [mobile,   setMobile]   = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate,   setToDate]   = useState("");
  const [currentPage, setCurrentPage] = useState(1);

  // ── api mutations ──
  const [getSessionMetrics,    { isLoading }]                 = useGetSessionMetricsMutation();
  const [getUserSessionDetail, { isLoading: isModalLoading }] = useGetUserSessionDetailMutation();

  // ── table data ──
  const [rows,       setRows]       = useState([]);
  const [pagination, setPagination] = useState({
    total: 0, total_pages: 1, current_page: 1,
    per_page: 20, has_next: false, has_prev: false,
  });

  // ── session detail modal state ──
  const [sessionModalOpen,     setSessionModalOpen]     = useState(false);
  const [modalUser,            setModalUser]            = useState(null);
  const [modalSessions,        setModalSessions]        = useState([]);

  // ── export report modal state ──
  const [exportModalOpen, setExportModalOpen] = useState(false);

  // ── fetch metrics — filters passed as explicit args to avoid stale closure ──
  const fetchMetrics = async (page = 1, filters = {}) => {
    const {
      user_id   = userId,
      mob       = mobile,
      from_date = fromDate,
      to_date   = toDate,
    } = filters;

    try {
      const res = await getSessionMetrics({
        token, tenant_id,
        user_id, mobile: mob, from_date, to_date,
        page, limit: 20,
      });
      if (res?.data?.success) {
        setRows(res.data.body.data || []);
        setPagination(res.data.body.pagination);
      } else {
        setRows([]);
      }
    } catch (err) {
      console.error("Failed to fetch session metrics", err);
      setRows([]);
    }
  };

  useEffect(() => { fetchMetrics(1, { user_id: "", mob: "", from_date: "", to_date: "" }); }, []);

  // ── open session detail modal ──
  const handleRowClick = async (row) => {
    setModalUser(row);
    setModalSessions([]);
    setSessionModalOpen(true);
    try {
      const res = await getUserSessionDetail({ token, tenant_id, app_user_id: row.app_user_id });
      if (res?.data?.success) setModalSessions(res.data.body.sessions_data || []);
    } catch (err) {
      console.error("Failed to fetch session detail", err);
    }
  };

  // ── search & reset — pass current values explicitly ──
  const handleSearch = () => {
    setCurrentPage(1);
    fetchMetrics(1, { user_id: userId, mob: mobile, from_date: fromDate, to_date: toDate });
  };

  const handleReset = () => {
    setUserId(""); setMobile(""); setFromDate(""); setToDate("");
    setCurrentPage(1);
    fetchMetrics(1, { user_id: "", mob: "", from_date: "", to_date: "" });
  };

  // ── pagination — pass current filter values ──
  const handlePrev = () => {
    if (!pagination.has_prev) return;
    const p = currentPage - 1; setCurrentPage(p);
    fetchMetrics(p, { user_id: userId, mob: mobile, from_date: fromDate, to_date: toDate });
  };
  const handleNext = () => {
    if (!pagination.has_next) return;
    const p = currentPage + 1; setCurrentPage(p);
    fetchMetrics(p, { user_id: userId, mob: mobile, from_date: fromDate, to_date: toDate });
  };
  const handlePageClick = (p) => {
    setCurrentPage(p);
    fetchMetrics(p, { user_id: userId, mob: mobile, from_date: fromDate, to_date: toDate });
  };

  const buildPageNumbers = () => {
    const total = pagination.total_pages, cur = currentPage;
    let start = Math.max(1, cur - 2), end = Math.min(total, start + 4);
    if (end - start < 4) start = Math.max(1, end - 4);
    const pages = [];
    for (let i = start; i <= end; i++) pages.push(i);
    return pages;
  };

  const startEntry = (currentPage - 1) * pagination.per_page + 1;
  const endEntry   = Math.min(currentPage * pagination.per_page, pagination.total);

  // ─────────────────────────────────────────────────────────────────────────────

  return (
    <>
      {/* session detail modal */}
      {sessionModalOpen && (
        <SessionDetailModal
          user={modalUser}
          sessions={modalSessions}
          isLoading={isModalLoading}
          onClose={() => setSessionModalOpen(false)}
        />
      )}

      {/* export report modal */}
      {exportModalOpen && (
        <ExportReportModal
          prefillUserId={userId}
          onClose={() => setExportModalOpen(false)}
        />
      )}

      <div style={{ display: "flex", width: "100%", background: "#f0f2f5", minHeight: "100vh" }}>
        <div style={{ width: "100%" }}>

          {/* ── page header ── */}
          <div style={{
            borderBottom: "1px solid #e0e0e0", background: "#fff",
            paddingLeft: 24, paddingTop: 10, paddingBottom: 10,
            boxShadow: "0 1px 3px rgba(0,0,0,0.07)",
            fontSize: 20, fontWeight: 700,
            fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif", color: "#1a202c",
          }}>
            User Session Metrics
            <LinksComponent />
          </div>

          <div style={{ padding: 16 }}>

            {/* ── filter card ── */}
            <div style={{
              background: "#fff", borderRadius: 14,
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              padding: "20px 24px 16px", marginBottom: 16,
              border: "1px solid #e8ecf0",
            }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
                <div style={{
                  width: 30, height: 30, borderRadius: 8,
                  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
                  display: "flex", alignItems: "center", justifyContent: "center",
                }}>
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <polygon points="22 3 2 3 10 12.46 10 19 14 21 14 12.46 22 3"/>
                  </svg>
                </div>
                <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 14, color: "#374151" }}>
                  Filter Session Metrics
                </span>
              </div>

              <div style={{ display: "flex", gap: 16, flexWrap: "wrap", alignItems: "flex-end" }}>

                {/* user id */}
                <div style={{ flex: "1 1 180px", minWidth: 160 }}>
                  <label style={labelStyle}>User ID</label>
                  <div style={{ position: "relative" }}>
                    <span style={inputIconStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/>
                      </svg>
                    </span>
                    <input type="text" placeholder="e.g. DLR123456" value={userId}
                      onChange={e => setUserId(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleSearch()}
                      style={inputStyle} />
                  </div>
                </div>

                {/* mobile */}
                <div style={{ flex: "1 1 180px", minWidth: 160 }}>
                  <label style={labelStyle}>Mobile Number</label>
                  <div style={{ position: "relative" }}>
                    <span style={inputIconStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="5" y="2" width="14" height="20" rx="2" ry="2"/><line x1="12" y1="18" x2="12.01" y2="18"/>
                      </svg>
                    </span>
                    <input type="text" placeholder="e.g. 9999999999" value={mobile}
                      onChange={e => setMobile(e.target.value)}
                      onKeyDown={e => e.key === "Enter" && handleSearch()}
                      style={inputStyle} />
                  </div>
                </div>

                {/* from date */}
                <div style={{ flex: "1 1 160px", minWidth: 150 }}>
                  <label style={labelStyle}>From Date</label>
                  <div style={{ position: "relative" }}>
                    <span style={inputIconStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </span>
                    <input type="date" value={fromDate} onChange={e => setFromDate(e.target.value)} style={inputStyle} />
                  </div>
                </div>

                {/* to date */}
                <div style={{ flex: "1 1 160px", minWidth: 150 }}>
                  <label style={labelStyle}>To Date</label>
                  <div style={{ position: "relative" }}>
                    <span style={inputIconStyle}>
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
                      </svg>
                    </span>
                    <input type="date" value={toDate} onChange={e => setToDate(e.target.value)} style={inputStyle} />
                  </div>
                </div>

                {/* buttons */}
                <div style={{ display: "flex", gap: 10 }}>
                  <button onClick={handleSearch} style={searchBtnStyle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                    </svg>
                    Search
                  </button>
                  <button onClick={handleReset} style={resetBtnStyle}>
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#6b7280" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <polyline points="1 4 1 10 7 10"/><path d="M3.51 15a9 9 0 1 0 .49-5.49"/>
                    </svg>
                    Reset
                  </button>
                </div>
              </div>
            </div>

            {/* ── results card ── */}
            <div style={{
              background: "#fff", borderRadius: 14,
              boxShadow: "0 2px 12px rgba(0,0,0,0.07)",
              border: "1px solid #e8ecf0", overflow: "hidden",
            }}>
              {/* results header bar */}
              <div style={{
                display: "flex", alignItems: "center", justifyContent: "space-between",
                padding: "14px 20px", borderBottom: "1px solid #f1f5f9", background: "#fafbfc",
              }}>
                <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                  <div style={{
                    width: 30, height: 30, borderRadius: 8,
                    background: "linear-gradient(135deg,#0ea5e9,#0284c7)",
                    display: "flex", alignItems: "center", justifyContent: "center",
                  }}>
                    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
                      <circle cx="9" cy="7" r="4"/>
                      <path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
                      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
                    </svg>
                  </div>
                  <span style={{ fontFamily: "'Plus Jakarta Sans',sans-serif", fontWeight: 700, fontSize: 14, color: "#374151" }}>
                    Session Metrics
                  </span>
                  <span style={{ fontSize: 12, color: "#9ca3af", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                    · click a row to view sessions
                  </span>
                </div>

                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <span style={{
                    background: "#eff6ff", color: "#1d4ed8", border: "1px solid #bfdbfe",
                    borderRadius: 20, padding: "3px 12px", fontSize: 12, fontWeight: 700,
                    fontFamily: "'DM Mono',monospace",
                  }}>
                    {pagination.total} user{pagination.total !== 1 ? "s" : ""}
                  </span>

                  {/* export button — opens modal */}
                  <button
                    onClick={() => setExportModalOpen(true)}
                    style={{
                      display: "flex", alignItems: "center", gap: 6,
                      background: "linear-gradient(135deg,#059669,#047857)",
                      color: "#fff", border: "none",
                      borderRadius: 8, padding: "7px 14px",
                      fontSize: 13, fontWeight: 700,
                      fontFamily: "'Plus Jakarta Sans',sans-serif",
                      cursor: "pointer",
                      boxShadow: "0 2px 8px rgba(5,150,105,0.30)",
                      transition: "all 0.2s", whiteSpace: "nowrap",
                    }}
                  >
                    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/>
                      <polyline points="7 10 12 15 17 10"/>
                      <line x1="12" y1="15" x2="12" y2="3"/>
                    </svg>
                    Export Report
                  </button>
                </div>
              </div>

              {/* ── table body ── */}
              {isLoading ? (
                <div style={{ display: "flex", justifyContent: "center", alignItems: "center", padding: 60 }}>
                  <CircularProgress size={36} style={{ color: "#2563eb" }} />
                </div>
              ) : rows.length === 0 ? (
                <div style={{ textAlign: "center", padding: "60px 20px" }}>
                  <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#d1d5db" strokeWidth="1.5"
                    strokeLinecap="round" strokeLinejoin="round" style={{ margin: "0 auto 12px", display: "block" }}>
                    <circle cx="11" cy="11" r="8"/><line x1="21" y1="21" x2="16.65" y2="16.65"/>
                  </svg>
                  <p style={{ color: "#9ca3af", fontFamily: "'Plus Jakarta Sans',sans-serif", fontSize: 14 }}>
                    No session data found for the selected filters.
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ overflowX: "auto" }}>
                    <table style={{ width: "100%", borderCollapse: "collapse", fontFamily: "'Plus Jakarta Sans','Segoe UI',sans-serif" }}>
                      <thead>
                        <tr style={{ background: "#f8fafc" }}>
                          {["#", "User ID", "User Name", "Mobile", "State", "Total Sessions", ""].map(h => (
                            <th key={h} style={thStyle}>{h}</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {rows.map((row, idx) => (
                          <tr
                            key={row.app_user_id}
                            onClick={() => handleRowClick(row)}
                            style={{ background: idx % 2 === 0 ? "#fff" : "#fafbfc", cursor: "pointer", transition: "background 0.15s" }}
                            onMouseEnter={e => e.currentTarget.style.background = "#eff6ff"}
                            onMouseLeave={e => e.currentTarget.style.background = idx % 2 === 0 ? "#fff" : "#fafbfc"}
                          >
                            <td style={{ ...tdStyle, color: "#9ca3af", fontFamily: "'DM Mono',monospace", fontSize: 12 }}>
                              {startEntry + idx}
                            </td>
                            <td style={tdStyle}>
                              <span style={{
                                fontFamily: "'DM Mono',monospace", fontWeight: 700,
                                color: "#1d4ed8", fontSize: 13,
                                background: "#eff6ff", borderRadius: 5, padding: "2px 7px",
                              }}>
                                {row.user_id || "—"}
                              </span>
                            </td>
                            <td style={{ ...tdStyle, fontWeight: 600, color: "#111827" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                <div style={{
                                  width: 30, height: 30, borderRadius: "50%", flexShrink: 0,
                                  background: "linear-gradient(135deg,#6366f1,#8b5cf6)",
                                  display: "flex", alignItems: "center", justifyContent: "center",
                                  fontSize: 12, fontWeight: 800, color: "#fff",
                                }}>
                                  {(row.user_name || "U").charAt(0).toUpperCase()}
                                </div>
                                <span style={{ fontSize: 13 }}>{row.user_name || "—"}</span>
                              </div>
                            </td>
                            <td style={{ ...tdStyle, fontFamily: "'DM Mono',monospace", fontSize: 13, color: "#374151" }}>
                              {row.mobile || "—"}
                            </td>
                            <td style={{ ...tdStyle, fontSize: 13, color: "#374151" }}>
                              <div style={{ display: "flex", alignItems: "center", gap: 5 }}>
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#9ca3af" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"/><circle cx="12" cy="10" r="3"/>
                                </svg>
                                {row.state || "—"}
                              </div>
                            </td>
                            <td style={tdStyle}>
                              <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                                <div style={{
                                  background: "linear-gradient(135deg,#7c3aed,#6d28d9)",
                                  color: "#fff", borderRadius: 8,
                                  padding: "4px 12px", fontSize: 13, fontWeight: 800,
                                  fontFamily: "'DM Mono',monospace",
                                  boxShadow: "0 2px 6px rgba(109,40,217,0.3)",
                                }}>
                                  {row.total_sessions_count ?? "—"}
                                </div>
                                <span style={{ fontSize: 11, color: "#9ca3af" }}>sessions</span>
                              </div>
                            </td>
                            {/* fixed-width last col so button doesn't stretch */}
                            <td style={{ ...tdStyle, textAlign: "right", width: "1%", whiteSpace: "nowrap" }}>
                              <span style={{
                                display: "inline-flex", alignItems: "center", gap: 4,
                                color: "#2563eb", fontSize: 12, fontWeight: 600,
                                background: "#eff6ff", border: "1px solid #bfdbfe",
                                borderRadius: 7, padding: "4px 10px",
                              }}>
                                View Sessions
                                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="#2563eb" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                                  <polyline points="9 18 15 12 9 6"/>
                                </svg>
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* pagination */}
                  <div style={{
                    display: "flex", justifyContent: "space-between", alignItems: "center",
                    padding: "12px 20px", borderTop: "1px solid #f1f5f9", background: "#fafbfc",
                  }}>
                    <span style={{ fontSize: 12, color: "#6b7280", fontFamily: "'Plus Jakarta Sans',sans-serif" }}>
                      Showing <b style={{ color: "#374151" }}>{pagination.total > 0 ? startEntry : 0}–{endEntry}</b> of <b style={{ color: "#374151" }}>{pagination.total}</b> users
                    </span>
                    <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
                      <button onClick={handlePrev} disabled={!pagination.has_prev} style={pageBtnStyle(!pagination.has_prev)}>
                        <MdKeyboardDoubleArrowLeft size={16} />
                      </button>
                      {buildPageNumbers().map(p => (
                        <button key={p} onClick={() => handlePageClick(p)} style={{
                          ...pageBtnStyle(false),
                          background: currentPage === p ? "#2563eb" : "#fff",
                          color:      currentPage === p ? "#fff"     : "#374151",
                          border:     currentPage === p ? "1px solid #2563eb" : "1px solid #e5e7eb",
                          fontWeight: currentPage === p ? 700 : 400,
                        }}>
                          {p}
                        </button>
                      ))}
                      <button onClick={handleNext} disabled={!pagination.has_next} style={pageBtnStyle(!pagination.has_next)}>
                        <MdKeyboardDoubleArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </>
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

// ─── styles ───────────────────────────────────────────────────────────────────

const labelStyle = {
  display: "block", fontSize: 12, fontWeight: 600, color: "#374151",
  marginBottom: 5, fontFamily: "'Plus Jakarta Sans',sans-serif", letterSpacing: 0.3,
};
const inputIconStyle = {
  position: "absolute", left: 10, top: "50%", transform: "translateY(-50%)",
  color: "#9ca3af", pointerEvents: "none", display: "flex",
};
const inputStyle = {
  width: "100%", paddingLeft: 32, paddingRight: 10, paddingTop: 8, paddingBottom: 8,
  border: "1.5px solid #e5e7eb", borderRadius: 8, fontSize: 13, color: "#111827",
  fontFamily: "'Plus Jakarta Sans',sans-serif", outline: "none",
  background: "#fafafa", boxSizing: "border-box",
};
const searchBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "linear-gradient(135deg,#2563eb,#1d4ed8)",
  color: "#fff", border: "none", borderRadius: 8, padding: "9px 18px",
  fontSize: 13, fontWeight: 700, fontFamily: "'Plus Jakarta Sans',sans-serif",
  cursor: "pointer", boxShadow: "0 2px 8px rgba(37,99,235,0.3)", whiteSpace: "nowrap",
};
const resetBtnStyle = {
  display: "flex", alignItems: "center", gap: 6,
  background: "#fff", color: "#6b7280", border: "1.5px solid #e5e7eb",
  borderRadius: 8, padding: "9px 16px", fontSize: 13, fontWeight: 600,
  fontFamily: "'Plus Jakarta Sans',sans-serif", cursor: "pointer", whiteSpace: "nowrap",
};
const thStyle = {
  padding: "10px 16px", textAlign: "left", fontSize: 11, fontWeight: 700,
  color: "#6b7280", textTransform: "uppercase", letterSpacing: 0.8,
  borderBottom: "2px solid #e5e7eb", whiteSpace: "nowrap",
  fontFamily: "'Plus Jakarta Sans',sans-serif",
};
const tdStyle = {
  padding: "12px 16px", fontSize: 13, color: "#374151",
  borderBottom: "1px solid #f1f5f9", verticalAlign: "middle",
};
const pageBtnStyle = (disabled) => ({
  display: "flex", alignItems: "center", justifyContent: "center",
  width: 32, height: 32, borderRadius: 7, border: "1px solid #e5e7eb",
  background: "#fff", color: disabled ? "#d1d5db" : "#374151",
  cursor: disabled ? "not-allowed" : "pointer",
  transition: "all 0.15s", opacity: disabled ? 0.5 : 1,
});

export default UserLoginLogs;
